/**
 * useAssistant Hook
 * Main hook that combines connection and command processing
 * Automatically handles tool execution requests from the AI assistant
 */

import { useEffect, useRef } from 'react';
import { useAssistantConnection } from './useAssistantConnection';
import { useCommandProcessor } from './useCommandProcessor';

interface UseAssistantProps {
  pipelineId: number;
  provider?: string;
}

export function useAssistant({ pipelineId, provider }: UseAssistantProps) {
  // Get connection hook
  const connection = useAssistantConnection({ pipelineId, provider });

  // Get command processor
  const { executeToolCall } = useCommandProcessor();

  // Track tools currently being executed to prevent duplicates
  const executingTools = useRef<Set<string>>(new Set());

  // Auto-execute pending tool calls
  useEffect(() => {
    const pendingTools = Array.from(connection.pendingToolCalls.entries());

    // Find tools that are pending (not yet executed or executing)
    const toolsToExecute = pendingTools.filter(
      ([requestId, toolCall]) => 
        toolCall.status === 'pending' && !executingTools.current.has(requestId)
    );

    // Execute each pending tool
    toolsToExecute.forEach(async ([requestId, toolCall]) => {
      // Mark as executing to prevent duplicate execution
      executingTools.current.add(requestId);

      console.log(`Executing tool: ${toolCall.tool_name}`, toolCall.tool_params);

      try {
        // Execute the tool
        const result = await executeToolCall(toolCall);

        // Send response back to backend
        connection.respondToToolRequest(
          requestId,
          result.success,
          result.result,
          result.error
        );

        console.log(`Tool ${toolCall.tool_name} completed:`, result);
      } catch (error) {
        console.error(`Tool ${toolCall.tool_name} failed:`, error);

        // Send error response
        connection.respondToToolRequest(
          requestId,
          false,
          undefined,
          error instanceof Error ? error.message : 'Unknown error'
        );
      } finally {
        // Remove from executing set
        executingTools.current.delete(requestId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection.pendingToolCalls, executeToolCall]);

  return connection;
}
